import { getFootballSeasonYear } from '../../utils/season';
// Idempotent create-or-update for gameAnalysis docs using a sticky client id.
// Guarantees:
//  • No cross-team/season/group reuse
//  • Never resurrects a deleted sticky doc
//  • Uses set() (not create()) + transactions
//  • Timestamps updated consistently
//
// Enhancements:
//  • SSR-safe guards for window/firebase/localStorage
//  • Stable serverTS() with fallback
//  • Legacy fanType normalization (expert → diehard)
//  • Structured logging via logger

import { getSticky, setSticky, clearSticky } from '../utils/sticky';
import { logger } from '../utils/logger';
import { addDoc, collection } from 'firebase/firestore';

// ─────────────────────────────────────────────────────────────────────────────
// Safe helpers
// ─────────────────────────────────────────────────────────────────────────────

const hasWindow = typeof window !== 'undefined';
const hasFirebase =
  hasWindow &&
  window.firebase &&
  window.firebase.firestore &&
  window.firebase.firestore.FieldValue;

const serverTS = () =>
  hasFirebase && window.firebase.firestore.FieldValue.serverTimestamp
    ? window.firebase.firestore.FieldValue.serverTimestamp()
    : null;

const nowFallback = () => new Date();

/** Convert various timestamp shapes to ms since epoch (defensive). */
const tsToMs = (v) => {
  if (!v) return 0;
  // Firestore Timestamp
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  // Date
  if (v instanceof Date) return v.getTime();
  // Number (ms)
  if (typeof v === 'number') return v;
  // String / unknown
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

export const updatedAtOf = (a = {}) =>
  Math.max(
    tsToMs(a.updatedAt),
    tsToMs(a.lastUpdated),
    tsToMs(a.updated_at),
    tsToMs(a.createdAt)
  );

/** Normalize legacy/variant fan types. */
const normalizeFanType = (fanType) => {
  if (!fanType) return null;
  const map = {
    expert: 'diehard',
    expertAnalyst: 'diehard',
    dedicatedFan: 'dedicated',
  };
  return map[fanType] || fanType;
};


// ─────────────────────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Find a recent draft/in_progress in the same context (team/season and, if present, group).
 * Returns { id, ...data, _ref } or null
 */
const findSameContextDraft = async (db, uid, { team, teamId, season, groupId }) => {
  const seasonNum = Number(season || getFootballSeasonYear());

  // Note: where('status', 'in', ...) + orderBy('activityAt') requires a composite index.
  const q = db
    .collection('gameAnalysis')
    .where('userId', '==', uid)
    .where('status', 'in', ['draft', 'in_progress'])
    .orderBy('activityAt', 'desc')
    .limit(8);

  const snap = await q.get();
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data(), _ref: d.ref }));

  const match = rows.find((r) => {
    const sameSeason = Number(r.season || 0) === seasonNum;
    const teamMatches =
      String(r.teamId || '') === String(teamId || '') ||
      String(r.team || '') === String(team || '');
    const groupMatches = !groupId || String(r.groupId || '') === String(groupId || '');
    return sameSeason && teamMatches && groupMatches;
  });

  return match || null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Create or reuse (context-safe, deletion-safe)
// ─────────────────────────────────────────────────────────────────────────────

export async function createGameIfNeeded(db, user, raw = {}) {
  if (!db || !user?.uid) {
    logger.error('createGameIfNeeded: Firestore or user not available');
    throw new Error('Firestore or user not available');
  }

  const uid = user.uid;

  // Context inputs
  const season = raw.season || getFootballSeasonYear();
  const team = raw.team ?? null;
  const teamId = raw.teamId ?? '';
  const league = raw.league ?? null;
  const groupId = raw.groupId ?? null;
  const groupName = raw.groupName ?? null;
  const groupMode = raw.groupMode ?? null;
  const isGroupCreator = !!raw.isGroupCreator;

  // Stickiness
  const callerSticky = raw._existingDocId || raw._clientOperationId || null;
  let stickyId;
  try {
    stickyId = callerSticky || getSticky();
  } catch {
    stickyId = callerSticky || null;
  }

  const normalizedFanType = normalizeFanType(raw.fanType);

  // Canonical time fields (prefer serverTS, fallback to local time)
  const tsNow = serverTS() || nowFallback();

  const baseDoc = {
    userId: uid,
    status: raw.status || 'draft',
    activityAt: tsNow, // 👈 single canonical activity clock

    // Analysis context
    team,
    teamId,
    league,
    conference: raw.conference || null,
    division: raw.division || '',
    season,

    // Group linkage (critical for results + security rules)
    groupId,
    groupName: groupName || null,
    groupMode: groupMode || null, // 'create' | 'join' | null
    isGroupCreator,

    // Profile
    participantName: (raw.participantName || '').trim(),
    avatar: raw.avatar || null,
    fanType: normalizedFanType,

    // Progress
    positionGrades: raw.positionGrades || {},
    coachGrades: raw.coachGrades || {},
    predictions: raw.predictions || {},

    _clientOperationId: stickyId || null,
    createdAt: serverTS() || raw.createdAt || nowFallback(),
    lastUpdated: tsNow, // keep for back-compat
  };

  const sameContext = (doc) => {
    const docSeason = Number(doc?.season || 0);
    const targetSeason = Number(season || getFootballSeasonYear());
    const teamMatches =
      String(doc?.teamId || '') === String(teamId || '') ||
      String(doc?.team || '') === String(team || '');
    const seasonOk = targetSeason === docSeason;
    const groupOk = !groupId || String(doc?.groupId || '') === String(groupId || '');
    return seasonOk && (!team ? true : teamMatches) && groupOk;
  };

  // ── Sticky reuse path ──────────────────────────────────────────────────────
  if (stickyId) {
    const ref = db.collection('gameAnalysis').doc(stickyId);
    try {
      await db.runTransaction(async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists) throw new Error('STICKY_MISSING');

        const cur = snap.data() || {};
        const hasContext = Boolean(cur.team || cur.teamId || cur.season || cur.groupId);

        if (hasContext && !sameContext(cur)) {
          throw new Error('STICKY_CONTEXT_MISMATCH');
        }

        // Only update safe fields; never stomp user progress.
        const updates = {
          activityAt: serverTS() || nowFallback(), // 👈 bump activity
          lastUpdated: serverTS() || nowFallback(), // back-compat
        };

        // Allow team/league/season refresh
        if (team !== null) {
          updates.team = team;
          updates.teamId = teamId;
          updates.league = league;
          updates.season = season;
        }

        // Group linkage if blank or matching (never switch groups)
        if (groupId && (!cur.groupId || String(cur.groupId) === String(groupId))) {
          updates.groupId = groupId;
          if (groupName) updates.groupName = groupName;
          if (groupMode) updates.groupMode = groupMode;
          updates.isGroupCreator = isGroupCreator;
        }

        if (baseDoc.participantName) updates.participantName = baseDoc.participantName;
        if (baseDoc.avatar !== null) updates.avatar = baseDoc.avatar;
        if (normalizedFanType) updates.fanType = normalizedFanType;

        tx.update(ref, updates);
      });

      try {
        setSticky(stickyId);
      } catch {}
      logger.success('♻️ createGameIfNeeded: Reused sticky doc', stickyId);
      return { id: stickyId, existed: true };
    } catch (e) {
      if (e?.message === 'STICKY_MISSING' || e?.message === 'STICKY_CONTEXT_MISMATCH') {
        // Don’t resurrect or cross contexts
        try {
          clearSticky();
        } catch {}
        logger.warn('🧹 Sticky cleared due to:', e?.message);
      } else {
        logger.error('createGameIfNeeded: sticky reuse failed:', e);
        throw e;
      }
    }
  }

  // ── Context draft reuse path ───────────────────────────────────────────────
  let reuse = null;
  if (team || teamId) {
    try {
      reuse = await findSameContextDraft(db, uid, { team, teamId, season, groupId });
    } catch (e) {
      logger.warn('createGameIfNeeded: probe for draft failed:', e);
    }
  }

  if (reuse) {
    const updates = {
      ...(baseDoc.participantName ? { participantName: baseDoc.participantName } : {}),
      ...(baseDoc.avatar !== null ? { avatar: baseDoc.avatar } : {}),
      ...(normalizedFanType ? { fanType: normalizedFanType } : {}),
      ...(groupId &&
      (!reuse.groupId || String(reuse.groupId) === String(groupId))
        ? {
            groupId,
            ...(groupName ? { groupName } : {}),
            ...(groupMode ? { groupMode } : {}),
            isGroupCreator,
          }
        : {}),
      activityAt: serverTS() || nowFallback(),
      lastUpdated: serverTS() || nowFallback(),
    };

    await reuse._ref.update(updates);
    try {
      setSticky(reuse.id);
    } catch {}
    logger.success('♻️ createGameIfNeeded: Reused context draft', reuse.id);
    return { id: reuse.id, existed: true };
  }

  // ── New doc path ───────────────────────────────────────────────────────────
  const collectionRef = collection(db, 'gameAnalysis');
  const newDocRef = await addDoc(collectionRef, {
    ...baseDoc,
    _createdVia: 'fresh',
  });
  const newId = newDocRef.id;

  try {
    setSticky(newId);
  } catch {}
  logger.success('🆕 createGameIfNeeded: Created new gameAnalysis', newId);
  return { id: newId, existed: false };
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────────────────────

export async function updateGameFields(db, { id, updates }) {
  if (!db || !id) throw new Error('updateGameFields requires id');
  const ref = db.collection('gameAnalysis').doc(id);

  const patch = {
    ...updates,
    activityAt: serverTS() || nowFallback(),
    lastUpdated: serverTS() || nowFallback(),
  };

  await ref.set(patch, { merge: true });
  logger.firebase('🔥 updateGameFields: updated', { id, keys: Object.keys(updates || {}) });
}

export async function markSubmitted(db, id, extra = {}) {
  if (!db || !id) throw new Error('markSubmitted requires id');
  const ref = db.collection('gameAnalysis').doc(id);

  const ts = serverTS() || nowFallback();

  await ref.set(
    {
      status: 'submitted',
      submittedAt: ts,
      activityAt: ts, // 👈 still counts as the latest activity
      ...extra,
      lastUpdated: ts,
    },
    { merge: true }
  );
  logger.success('🏁 markSubmitted: marked as submitted', id);
}

export async function deleteGameAnalysis(db, id) {
  if (!db || !id) throw new Error('deleteGameAnalysis requires id');
  await db.collection('gameAnalysis').doc(id).delete();
  try {
    const current = getSticky();
    if (current === id) clearSticky();
  } catch {}
  logger.warn('🗑️ deleteGameAnalysis: deleted and cleared sticky if matched', id);
}
